import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  type AlertChannel,
  type AlertCreateBody,
  type AlertCriteria,
  type AlertFrequency,
  type AlertQuietHours,
  type UserAlert,
  userApi,
} from '../../services/userApi';
import type { AlertDraft } from '../../services/searchToAlert';

const BULLET_TYPE_OPTIONS = ['FMJ', 'JHP', 'HP', 'SP', 'Match', 'Frangible', 'Lead'];
const FREQUENCY_OPTIONS: AlertFrequency[] = ['immediate', 'daily', 'weekly'];

interface FormState {
  name: string;
  calibers: string[];
  caliberDraft: string;
  brands: string[];
  brandDraft: string;
  bulletWeight: string;
  bulletType: string;
  maxCpr: string;
  minRoundCount: string;
  excludeReloaded: boolean;
  lotNumbers: string[];
  lotDraft: string;
  channels: AlertChannel[];
  frequency: AlertFrequency;
  quietHoursEnabled: boolean;
  quietStart: string;
  quietEnd: string;
  quietTimezone: string;
  maxPerDay: string;
  isActive: boolean;
}

function emptyForm(): FormState {
  return {
    name: '',
    calibers: [],
    caliberDraft: '',
    brands: [],
    brandDraft: '',
    bulletWeight: '',
    bulletType: '',
    maxCpr: '',
    minRoundCount: '',
    excludeReloaded: false,
    lotNumbers: [],
    lotDraft: '',
    channels: ['email'],
    frequency: 'immediate',
    quietHoursEnabled: false,
    quietStart: '22:00',
    quietEnd: '07:00',
    quietTimezone:
      typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
    maxPerDay: '',
    isActive: true,
  };
}

function fromAlert(alert: UserAlert): FormState {
  const empty = emptyForm();
  return {
    ...empty,
    name: alert.name,
    calibers: alert.criteria.caliber ?? [],
    brands: alert.criteria.brand ?? [],
    bulletWeight:
      Array.isArray(alert.criteria.bulletWeightGrains) && alert.criteria.bulletWeightGrains[0]
        ? String(alert.criteria.bulletWeightGrains[0])
        : '',
    bulletType: alert.criteria.bulletType?.[0] ?? '',
    maxCpr: alert.criteria.maxPricePerRound == null ? '' : String(alert.criteria.maxPricePerRound),
    minRoundCount: alert.criteria.minRoundCount == null ? '' : String(alert.criteria.minRoundCount),
    excludeReloaded: Boolean(alert.criteria.excludeReloaded),
    lotNumbers: alert.criteria.lotNumbers ?? [],
    channels: alert.channels,
    frequency: alert.frequency,
    quietHoursEnabled: Boolean(alert.quietHours?.enabled),
    quietStart: alert.quietHours?.start ?? '22:00',
    quietEnd: alert.quietHours?.end ?? '07:00',
    quietTimezone: alert.quietHours?.timezone ?? empty.quietTimezone,
    maxPerDay: alert.maxPerDay == null ? '' : String(alert.maxPerDay),
    isActive: alert.isActive,
  };
}

/**
 * Seed the form from a pre-populated draft (the "Convert to alert" flow) — a
 * new (unsaved) alert with criteria carried over from a saved search. Only the
 * criteria the form exposes are applied; the rest of the alert (channels,
 * frequency, quiet hours) keeps its sensible defaults for the user to confirm.
 */
function fromDraft(draft: AlertDraft): FormState {
  const empty = emptyForm();
  const c = draft.criteria;
  return {
    ...empty,
    name: draft.name,
    calibers: c.caliber ?? [],
    brands: c.brand ?? [],
    bulletWeight:
      Array.isArray(c.bulletWeightGrains) && c.bulletWeightGrains[0]
        ? String(c.bulletWeightGrains[0])
        : '',
    bulletType: c.bulletType?.[0] ?? '',
    maxCpr: c.maxPricePerRound == null ? '' : String(c.maxPricePerRound),
    minRoundCount: c.minRoundCount == null ? '' : String(c.minRoundCount),
    excludeReloaded: Boolean(c.excludeReloaded),
    lotNumbers: c.lotNumbers ?? [],
  };
}

interface ValidationErrors {
  name?: string;
  channels?: string;
  bulletWeight?: string;
  maxCpr?: string;
  minRoundCount?: string;
  maxPerDay?: string;
  quietStart?: string;
  quietEnd?: string;
  criteria?: string;
}

function validate(form: FormState): ValidationErrors {
  const errors: ValidationErrors = {};
  if (!form.name.trim()) errors.name = 'Give your alert a name.';
  if (form.channels.length === 0) {
    errors.channels = 'Choose at least one notification channel.';
  }
  if (
    form.bulletWeight !== '' &&
    (!Number.isFinite(Number(form.bulletWeight)) || Number(form.bulletWeight) <= 0)
  ) {
    errors.bulletWeight = 'Bullet weight must be a positive number.';
  }
  if (form.maxCpr !== '' && (!Number.isFinite(Number(form.maxCpr)) || Number(form.maxCpr) <= 0)) {
    errors.maxCpr = 'Max cost-per-round must be a positive number.';
  }
  if (
    form.minRoundCount !== '' &&
    (!Number.isInteger(Number(form.minRoundCount)) || Number(form.minRoundCount) <= 0)
  ) {
    errors.minRoundCount = 'Round count must be a positive whole number.';
  }
  if (
    form.maxPerDay !== '' &&
    (!Number.isInteger(Number(form.maxPerDay)) || Number(form.maxPerDay) <= 0)
  ) {
    errors.maxPerDay = 'Max per day must be a positive whole number.';
  }
  if (form.quietHoursEnabled) {
    if (!/^\d{2}:\d{2}$/.test(form.quietStart)) errors.quietStart = 'Use 24-hour HH:mm.';
    if (!/^\d{2}:\d{2}$/.test(form.quietEnd)) errors.quietEnd = 'Use 24-hour HH:mm.';
  }
  if (
    form.calibers.length === 0 &&
    form.brands.length === 0 &&
    !form.bulletWeight &&
    !form.bulletType &&
    !form.maxCpr &&
    !form.minRoundCount &&
    form.lotNumbers.length === 0 &&
    !form.excludeReloaded
  ) {
    errors.criteria = 'Set at least one criterion so the alert can match listings.';
  }
  return errors;
}

function toBody(form: FormState): AlertCreateBody {
  const criteria: AlertCriteria = {};
  if (form.calibers.length > 0) criteria.caliber = form.calibers;
  if (form.brands.length > 0) criteria.brand = form.brands;
  if (form.bulletWeight !== '') criteria.bulletWeightGrains = [Number(form.bulletWeight)];
  if (form.bulletType !== '') criteria.bulletType = [form.bulletType];
  if (form.maxCpr !== '') criteria.maxPricePerRound = Number(form.maxCpr);
  if (form.minRoundCount !== '') criteria.minRoundCount = Number(form.minRoundCount);
  if (form.excludeReloaded) criteria.excludeReloaded = true;
  if (form.lotNumbers.length > 0) criteria.lotNumbers = form.lotNumbers;

  const quietHours: AlertQuietHours | null = form.quietHoursEnabled
    ? {
        enabled: true,
        start: form.quietStart,
        end: form.quietEnd,
        timezone: form.quietTimezone || undefined,
      }
    : null;

  return {
    name: form.name.trim(),
    criteria,
    channels: form.channels,
    frequency: form.frequency,
    quietHours,
    maxPerDay: form.maxPerDay === '' ? null : Number(form.maxPerDay),
    isActive: form.isActive,
  };
}

export interface AlertFormDialogProps {
  open: boolean;
  alert?: UserAlert | null;
  /**
   * Pre-populate a NEW alert from a saved search ("Convert to alert"). Ignored
   * when `alert` is set (edit mode wins).
   */
  initialDraft?: AlertDraft | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Create/edit dialog for an Alert (spec §13.4 + §8 deduplication and
 * quiet-hours flags). Renders as a modal with semantic fieldsets so
 * screen readers can announce each criteria group.
 */
export function AlertFormDialog({
  open,
  alert,
  initialDraft,
  onClose,
  onSaved,
}: AlertFormDialogProps) {
  const isEdit = Boolean(alert);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset the form each time the dialog opens (or the target alert / pre-fill
  // draft changes while open). "Adjust state during render" on the transition
  // instead of a setState-in-effect.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevAlert, setPrevAlert] = useState(alert);
  const [prevDraft, setPrevDraft] = useState(initialDraft);
  if (open && (!prevOpen || alert !== prevAlert || initialDraft !== prevDraft)) {
    setPrevOpen(true);
    setPrevAlert(alert);
    setPrevDraft(initialDraft);
    setForm(alert ? fromAlert(alert) : initialDraft ? fromDraft(initialDraft) : emptyForm());
    setErrors({});
    setSubmitError(null);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function addChip(field: 'calibers' | 'brands' | 'lotNumbers', draftKey: keyof FormState) {
    const draft = String(form[draftKey] ?? '').trim();
    if (!draft) return;
    setForm((f) => ({
      ...f,
      [field]: [...(f[field] as string[]), draft],
      [draftKey]: '' as never,
    }));
  }

  function removeChip(field: 'calibers' | 'brands' | 'lotNumbers', idx: number) {
    setForm((f) => ({
      ...f,
      [field]: (f[field] as string[]).filter((_, i) => i !== idx),
    }));
  }

  function toggleChannel(channel: AlertChannel) {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(channel)
        ? f.channels.filter((c) => c !== channel)
        : [...f.channels, channel],
    }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validate(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = toBody(form);
      if (isEdit && alert) {
        await userApi.updateAlert(alert.id, body);
      } else {
        await userApi.createAlert(body);
      }
      onSaved();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save alert.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="alert-form-title"
    >
      <DialogTitle id="alert-form-title">{isEdit ? 'Edit alert' : 'New alert'}</DialogTitle>
      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent dividers>
          {submitError ? (
            <MuiAlert severity="error" role="alert" sx={{ mb: 2 }}>
              {submitError}
            </MuiAlert>
          ) : null}

          <Stack spacing={3}>
            <TextField
              label="Alert name"
              required
              fullWidth
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              error={Boolean(errors.name)}
              helperText={errors.name ?? 'A short name so you can recognize it in lists.'}
            />

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Criteria
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <ChipInput
                  label="Calibers"
                  helper="Press Enter to add. Use canonical names like 9mm Luger, .223 Remington."
                  values={form.calibers}
                  draft={form.caliberDraft}
                  onDraftChange={(v) => update('caliberDraft', v)}
                  onAdd={() => addChip('calibers', 'caliberDraft')}
                  onRemove={(idx) => removeChip('calibers', idx)}
                />
                <ChipInput
                  label="Brands"
                  helper="Brand names to include (Federal, Hornady, etc.)."
                  values={form.brands}
                  draft={form.brandDraft}
                  onDraftChange={(v) => update('brandDraft', v)}
                  onAdd={() => addChip('brands', 'brandDraft')}
                  onRemove={(idx) => removeChip('brands', idx)}
                />
                <TextField
                  label="Bullet weight (grains)"
                  type="number"
                  fullWidth
                  value={form.bulletWeight}
                  onChange={(e) => update('bulletWeight', e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                  error={Boolean(errors.bulletWeight)}
                  helperText={errors.bulletWeight ?? ' '}
                />
                <FormControl fullWidth>
                  <InputLabel id="bullet-type-label">Bullet type</InputLabel>
                  <Select
                    labelId="bullet-type-label"
                    id="bullet-type"
                    label="Bullet type"
                    value={form.bulletType}
                    onChange={(e) => update('bulletType', String(e.target.value))}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {BULLET_TYPE_OPTIONS.map((b) => (
                      <MenuItem key={b} value={b}>
                        {b}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Max cost per round (USD)"
                  type="number"
                  fullWidth
                  value={form.maxCpr}
                  onChange={(e) => update('maxCpr', e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }}
                  error={Boolean(errors.maxCpr)}
                  helperText={errors.maxCpr ?? 'Listings above this CPR are ignored.'}
                />
                <TextField
                  label="Minimum round count"
                  type="number"
                  fullWidth
                  value={form.minRoundCount}
                  onChange={(e) => update('minRoundCount', e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  error={Boolean(errors.minRoundCount)}
                  helperText={errors.minRoundCount ?? ' '}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.excludeReloaded}
                      onChange={(e) => update('excludeReloaded', e.target.checked)}
                    />
                  }
                  label="Exclude remanufactured / reloaded ammo"
                />
                <ChipInput
                  label="Lot numbers to watch"
                  helper="Optional. Useful when watching a specific batch."
                  values={form.lotNumbers}
                  draft={form.lotDraft}
                  onDraftChange={(v) => update('lotDraft', v)}
                  onAdd={() => addChip('lotNumbers', 'lotDraft')}
                  onRemove={(idx) => removeChip('lotNumbers', idx)}
                />
                {errors.criteria ? <FormHelperText error>{errors.criteria}</FormHelperText> : null}
              </Stack>
            </Box>

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Notifications
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.channels.includes('email')}
                      onChange={() => toggleChannel('email')}
                    />
                  }
                  label="Email"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.channels.includes('in_app')}
                      onChange={() => toggleChannel('in_app')}
                    />
                  }
                  label="In-app"
                />
              </FormGroup>
              {errors.channels ? <FormHelperText error>{errors.channels}</FormHelperText> : null}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  id="frequency"
                  label="Frequency"
                  value={form.frequency}
                  onChange={(e) => update('frequency', e.target.value as AlertFrequency)}
                >
                  {FREQUENCY_OPTIONS.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Max notifications per day"
                type="number"
                fullWidth
                sx={{ mt: 2 }}
                value={form.maxPerDay}
                onChange={(e) => update('maxPerDay', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                error={Boolean(errors.maxPerDay)}
                helperText={errors.maxPerDay ?? 'Leave blank for no cap.'}
              />
            </Box>

            <Box
              component="fieldset"
              sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1 }}
            >
              <Typography component="legend" variant="subtitle1">
                Quiet hours
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.quietHoursEnabled}
                    onChange={(e) => update('quietHoursEnabled', e.target.checked)}
                  />
                }
                label="Enable quiet hours"
              />
              {form.quietHoursEnabled ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    label="Start (HH:mm)"
                    value={form.quietStart}
                    onChange={(e) => update('quietStart', e.target.value)}
                    error={Boolean(errors.quietStart)}
                    helperText={errors.quietStart ?? ' '}
                    fullWidth
                  />
                  <TextField
                    label="End (HH:mm)"
                    value={form.quietEnd}
                    onChange={(e) => update('quietEnd', e.target.value)}
                    error={Boolean(errors.quietEnd)}
                    helperText={errors.quietEnd ?? ' '}
                    fullWidth
                  />
                  <TextField
                    label="Timezone"
                    value={form.quietTimezone}
                    onChange={(e) => update('quietTimezone', e.target.value)}
                    fullWidth
                  />
                </Stack>
              ) : null}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => update('isActive', e.target.checked)}
                />
              }
              label="Alert is active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} aria-busy={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create alert'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

interface ChipInputProps {
  label: string;
  helper: string;
  values: string[];
  draft: string;
  onDraftChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}

function ChipInput({
  label,
  helper,
  values,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
}: ChipInputProps) {
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }}>
        {values.map((v, idx) => (
          <Chip
            key={`${v}-${idx}`}
            label={v}
            onDelete={() => onRemove(idx)}
            aria-label={`Remove ${label} ${v}`}
            sx={{ mb: 1 }}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          label={label}
          size="small"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd();
            }
          }}
          helperText={helper}
          fullWidth
        />
        <Button variant="outlined" onClick={onAdd}>
          Add
        </Button>
      </Stack>
    </Box>
  );
}

export default AlertFormDialog;
